import { getApiUrl } from '@/config/runtime-env'
import { getAccessToken } from '@/utils/tokenService'

const API_BASE_URL = getApiUrl()

/**
 * Upload file through backend proxy (solves CORS issues)
 */
export async function uploadFile(file: File): Promise<{
  url: string
  fileId?: string
}> {
  try {
    // Create FormData for multipart upload
    const formData = new FormData()
    formData.append('file', file)
    formData.append('bucketName', 'images')
    formData.append('metadata', JSON.stringify({
      category: 'college',
      purpose: 'branding',
    }))
    formData.append('tags', JSON.stringify(['college', 'admin-upload']))

    // Get auth token
    const token = getAccessToken()
    
    // Upload through backend proxy
    const response = await fetch(`${API_BASE_URL}/storage/upload`, {
      method: 'POST',
      headers: {
        ...(token && { 'Authorization': `Bearer ${token}` }),
        // Don't set Content-Type - let browser handle multipart form data
      },
      body: formData,
    })

    if (!response.ok) {
      const error = await response.text()
      throw new Error(`Upload failed: ${error}`)
    }

    const result = await response.json()
    const fileId = result.data?.id || result.id

    if (!fileId) {
      throw new Error('No file ID returned from upload')
    }

    // Return the file ID as the URL - the backend will handle resolution
    // This way we store the file ID in the database and resolve it on demand
    return {
      url: fileId,  // Pass file ID as URL
      fileId,
    }
  } catch (error) {
    console.error('File upload failed:', error)
    throw error
  }
}

/**
 * Upload an attachment for an AI chat session (admin manual-mode replies).
 * Posts to the admin chat upload endpoint, which stores the file with chat
 * context so it can be presigned for WhatsApp delivery and admin rendering.
 */
export async function uploadChatAttachment(
  sessionId: string,
  file: File,
): Promise<{ fileId: string; fileName: string }> {
  const formData = new FormData()
  formData.append('file', file)

  const token = getAccessToken()

  const response = await fetch(
    `${API_BASE_URL}/admin/ai-chat-sessions/${sessionId}/upload-file`,
    {
      method: 'POST',
      headers: {
        ...(token && { Authorization: `Bearer ${token}` }),
        // No Content-Type — let the browser set the multipart boundary.
      },
      body: formData,
    },
  )

  if (!response.ok) {
    const error = await response.text()
    throw new Error(`Upload failed: ${error}`)
  }

  const result = await response.json()
  const data = result.data || result
  const fileId = data.fileId || data.id
  if (!fileId) {
    throw new Error('No file ID returned from upload')
  }
  return { fileId, fileName: data.fileName || file.name }
}

/**
 * Get download URL for a file
 */
export async function getDownloadUrl(
  fileId: string,
  expiresIn: number = 3600
): Promise<string | null> {
  if (!fileId) return null

  try {
    const token = getAccessToken()
    
    const response = await fetch(
      `${API_BASE_URL}/storage/download-url/${fileId}?expiresIn=${expiresIn}`,
      {
        method: 'GET',
        headers: {
          ...(token && { 'Authorization': `Bearer ${token}` }),
        },
      }
    )

    if (!response.ok) {
      console.error('Failed to get download URL:', response.statusText)
      return null
    }

    const responseData = await response.json()
    // Handle nested response structure if API returns { data: {...} }
    const data = responseData.data || responseData
    return data.downloadUrl || data.url || null
  } catch (error) {
    console.error('Error getting download URL:', error)
    return null
  }
}

/**
 * Validates an image file
 */
export function validateImageFile(file: File, maxSizeMB: number = 10): {
  isValid: boolean
  errors: string[]
} {
  const errors: string[] = []

  // Check file size
  const maxSize = maxSizeMB * 1024 * 1024
  if (file.size > maxSize) {
    errors.push(`File size must be less than ${maxSizeMB}MB`)
  }

  // Check file type
  const allowedTypes = ['image/jpeg', 'image/png', 'image/webp', 'image/gif']
  if (!allowedTypes.includes(file.type)) {
    errors.push('File must be an image (JPEG, PNG, WebP, or GIF)')
  }

  // Check file name
  const invalidChars = /[<>:"/\\|?*]/
  if (invalidChars.test(file.name)) {
    errors.push('File name contains invalid characters')
  }

  return {
    isValid: errors.length === 0,
    errors,
  }
}

/**
 * Bulk resolve file IDs to download URLs
 */
export async function bulkResolveFileUrls(
  fileIds: (string | null | undefined)[],
  expiresIn: number = 3600
): Promise<Record<string, string | null>> {
  // Filter out null/undefined file IDs
  const validFileIds = fileIds.filter(Boolean) as string[]
  
  if (validFileIds.length === 0) {
    return {}
  }

  const result: Record<string, string | null> = {}

  try {
    // Resolve each file ID to a download URL
    await Promise.all(
      validFileIds.map(async (fileId) => {
        const url = await getDownloadUrl(fileId, expiresIn)
        result[fileId] = url
      })
    )

    return result
  } catch (error) {
    console.error('Failed to bulk resolve file URLs:', error)
    
    // Return null for all requested file IDs on error
    validFileIds.forEach(fileId => {
      result[fileId] = null
    })
    
    return result
  }
}